import { useEffect, useRef, useState } from 'react';
import fleetApi from '../../services/api.service';
import { buildUrlWithParams } from '../../utils/url.utils';

const SEARCH_DEBOUNCE_MS = 400;

/**
 * Liste paginée des envois AS400 (page "Suivi des retours de tournée"), avec filtres et défilement infini.
 * reload() recharge la première page avec les filtres courants (après une action).
 */
export default function useGetTourLogs() {
  const [logs, setLogs] = useState(null);
  const [error, setError] = useState('');
  // Échec du chargement d'une page suivante : la liste reste affichée, la page est à recharger
  const [nextPageError, setNextPageError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Position atteinte côté serveur : la page suivante en part, même si des doublons ont été écartés
  const [nextOffset, setNextOffset] = useState(0);
  const abortControllerRef = useRef(null);
  // Un rechargement est programmé (recherche en cours de saisie) : la page suivante attend
  const isFetchScheduledRef = useRef(false);

  const [filters, setFilters] = useState({
    limit: 20,
    offset: 0,
    query: '',
    state: '',
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    const delay =
      filters.offset === 0 && filters.query ? SEARCH_DEBOUNCE_MS : 0;
    isFetchScheduledRef.current = true;
    const timeout = setTimeout(() => getTourLogs(filters), delay);
    return () => clearTimeout(timeout);
  }, [filters, reloadKey]);

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  const updateFilter = (key, value) => {
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters, [key]: value };
      if (key !== 'offset') {
        newFilters.offset = 0;
      }
      return newFilters;
    });
  };

  const getTourLogs = async (currentFilters) => {
    isFetchScheduledRef.current = false;
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      setIsLoading(true);
      setError('');
      setNextPageError('');

      const params = Object.fromEntries(
        Object.entries(currentFilters).filter(
          ([, value]) => value !== '' && value !== null && value !== undefined
        )
      );
      const response = await fleetApi.get(
        buildUrlWithParams('/tours/tours-logs', params),
        { signal: abortController.signal }
      );

      // Des envois arrivés entre deux pages décalent la liste : les lignes déjà affichées sont écartées
      setLogs((prevData) => {
        if (!prevData || currentFilters.offset === 0) {
          return response.data;
        }
        const loadedIds = new Set(prevData.items.map((item) => item.id));
        return {
          ...response.data,
          items: [
            ...prevData.items,
            ...response.data.items.filter((item) => !loadedIds.has(item.id)),
          ],
        };
      });
      // Une page vide termine la liste (le total a baissé entre deux pages)
      setNextOffset(
        response.data.items.length
          ? currentFilters.offset + response.data.items.length
          : response.data.pagination_info.total_items
      );
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Une erreur est survenue.';
      if (currentFilters.offset > 0) {
        setNextPageError(message);
      } else {
        setError(message);
      }
    } finally {
      if (!abortController.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  // nextOffset n'avance qu'après une page reçue : une page en échec n'est jamais sautée
  const hasMore = logs
    ? !nextPageError && nextOffset < logs.pagination_info.total_items
    : false;

  const fetchNextPage = () => {
    if (!isLoading && !isFetchScheduledRef.current && hasMore) {
      updateFilter('offset', nextOffset);
    }
  };

  const retryNextPage = () => {
    getTourLogs({ ...filters, offset: nextOffset });
  };

  const reload = () => {
    setFilters((prevFilters) => ({ ...prevFilters, offset: 0 }));
    setReloadKey((key) => key + 1);
  };

  return {
    logs,
    error,
    isLoading,
    isFirstLoading: isLoading && !logs,
    fetchNextPage,
    hasMore,
    nextPageError,
    retryNextPage,
    filters,
    updateFilter,
    reload,
  };
}
